import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";

/**
 * Lightweight consent banner shown once per browser. We don't run any
 * analytics or third-party cookies today — only first-party local storage
 * for the auth token. The banner is therefore informational and acts as
 * "soft" consent: dismiss = acknowledge, no separate analytics opt-in.
 *
 * Persistence: a single key `sb_consent_v1` in localStorage. Bumping the
 * version (e.g., `sb_consent_v2`) will re-show the banner to everyone.
 */
const STORAGE_KEY = "sb_consent_v1";

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  // Decide once on mount whether to show the banner. Using a short delay
  // prevents the banner from flashing during initial route loads.
  useEffect(() => {
    let alive = true;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => { if (alive) setVisible(true); }, 600);
        return () => { alive = false; clearTimeout(t); };
      }
    } catch {
      // Private mode or storage blocked — don't bug the user.
    }
    return () => { alive = false; };
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: new Date().toISOString() })); } catch (e) {
      void e;
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 max-w-md animate-in fade-in slide-in-from-bottom-3 duration-500"
      role="dialog"
      aria-label="Cookie and storage notice"
      data-testid="consent-banner"
    >
      <div className="bg-white border border-slate-200/70 rounded-3xl shadow-floating p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-2xl bg-[#FEF3C7] flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-[#92400E]" />
          </span>
          <div className="min-w-0">
            <p className="font-heading font-bold text-foreground">We use a secure cookie to keep you signed in.</p>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              SchoolBoss does not use advertising or cross-site tracking cookies. We only set a single httpOnly session cookie so your account stays signed in. See our{" "}
              <Link to="/privacy" className="text-[#2563EB] font-semibold hover:underline">Privacy Policy</Link>.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                onClick={accept}
                className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold h-10 px-5 shadow-floating"
                data-testid="consent-accept-btn"
              >
                Got it
              </Button>
              <Link to="/privacy" data-testid="consent-learn-link">
                <Button variant="outline" className="rounded-full border-slate-200 h-10 px-5">
                  Learn more
                </Button>
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={accept}
            aria-label="Dismiss"
            className="ml-1 -mt-1 text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="consent-dismiss-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
